import React from 'react';
import { MapPin, Navigation, Star, Play, Smartphone } from 'lucide-react';

const TEAL = '#00A878';
const DARK = '#0D1117';
const WHITE = '#FFFFFF';
const MUTED = '#6c757d';

export default function About() {
  return (
    <div style={{ background: WHITE }}>

      {/* ── HERO ── */}
      <div className="section-pad-lg" style={{ background: WHITE, padding: '80px 0 60px' }}>
        <div className="container">
          <div className="row align-items-start g-5 mb-5">
            <div className="col-lg-6">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  WE ARE SPOT FINDER
                </span>
              </div>
              <h1 style={{
                fontWeight: '900', fontSize: '2.8rem',
                lineHeight: 1.15, letterSpacing: '-1px',
                color: DARK, marginTop: '0',
              }}>
                We set out to build<br />
                <span style={{ color: '#adb5bd' }}>a smarter way to park</span>
              </h1>
            </div>
            <div className="col-lg-4 offset-lg-2 d-flex align-items-end" style={{ paddingBottom: '8px' }}>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                Together — the drivers, operators, and partners of Spot Finder — we are reinventing urban parking in Nairobi end-to-end, making it effortless for everyone.
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-7">
              <div className="about-hero-img" style={{ borderRadius: '16px', overflow: 'hidden', height: '380px' }}>
                <img
                  src="/images/people-on-spotfinder.webp"
                  alt="People using Spot Finder app in Nairobi"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
            <div className="col-md-5">
              <div className="about-hero-img" style={{ borderRadius: '16px', overflow: 'hidden', height: '380px' }}>
                <img
                  src="/images/spotfinder-office.webp"
                  alt="Spot Finder office and team"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── OUR STORY ── */}
      <div className="section-pad-lg" style={{ background: WHITE, padding: '100px 0' }}>
        <div className="container">
          <div className="row g-5 mb-5">
            <div className="col-lg-5">
              <h2 style={{
                fontWeight: '900', fontSize: '2.8rem',
                color: DARK, letterSpacing: '-1px', lineHeight: 1.15,
              }}>
                Our Story ↗
              </h2>
            </div>
            <div className="col-lg-7">
              <p style={{ color: DARK, fontSize: '1rem', lineHeight: 1.8, fontWeight: '600', marginBottom: '20px' }}>
                Our founders started Spot Finder because they were tired of circling blocks in Nairobi looking for parking — and wanted to make it easy for anyone to find a spot instantly.
              </p>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.85, marginBottom: '16px' }}>
                They wanted to make sure that every driver — whether heading to a meeting in Westlands, shopping in Kilimani, or working in Upper Hill — would have the same opportunity to park quickly and affordably, from their phone.
              </p>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.85, marginBottom: '16px' }}>
                This vision extended to helping parking operators fill their bays, helping the city reduce traffic caused by drivers searching for spots, and building a platform that works for everyone in Nairobi.
              </p>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.85 }}>
                Finding a parking spot should be the easiest part of your day. The Spot Finder team is united around one core philosophy: find fast, park smart, and let M-Pesa handle the rest.
              </p>
            </div>
          </div>

          <div style={{ height: '1px', background: '#e9ecef', margin: '20px 0 48px' }} />

          <div className="row">
            <div className="col-lg-7 offset-lg-5">
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #E8F5F1, #B2DFDB)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <MapPin size={18} color={TEAL} aria-hidden="true" />
                </div>
                <div>
                  <div style={{ fontWeight: '700', color: DARK, fontSize: '14px' }}>Kefas Okaka</div>
                  <div style={{ color: MUTED, fontSize: '13px' }}>Founder & CEO</div>
                </div>
              </div>
              <p className="story-quote" style={{
                fontSize: '1.2rem', fontWeight: '700', color: DARK,
                lineHeight: 1.6, marginTop: '20px',
                borderLeft: `3px solid ${TEAL}`, paddingLeft: '20px',
              }}>
                "Our goal is to build a better parking system for every Nairobi driver — one that is simpler, faster, more affordable, and always available."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── NUMBERS ── */}
      <div className="section-pad-lg" style={{ background: '#F5F5F5', padding: '100px 0' }}>
        <div className="container">
          <div style={{
            display: 'inline-block',
            background: '#E8F5F1', border: '1px solid #B2DFDB',
            borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
          }}>
            <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
              NUMBERS SPEAK FOR THEMSELVES
            </span>
          </div>
          <h2 style={{
            fontWeight: '900', fontSize: '2.8rem',
            color: DARK, letterSpacing: '-1px', lineHeight: 1.15,
            marginBottom: '48px',
          }}>
            Growing fast<br />across Nairobi
          </h2>

          {[
            { num: '5,000+', desc: "Our platform covers over 5,000 verified parking bays across Nairobi's busiest zones — and we add new ones every month." },
            { num: 'KSh 450M+', desc: 'We have helped process over KSh 450 million in parking transactions, saving drivers time and operators revenue.' },
            { num: '60%', desc: 'Drivers using Spot Finder reduce their parking search time by an average of 60% compared to driving around manually.' },
          ].map(({ num, desc }, i) => (
            <div key={num}>
              <div className="row align-items-center g-5 py-4">
                <div className="col-lg-4">
                  <div className="about-numbers-num" style={{
                    fontSize: '3.5rem', fontWeight: '900',
                    color: DARK, letterSpacing: '-2px', lineHeight: 1,
                  }}>
                    {num}
                  </div>
                </div>
                <div className="col-lg-8">
                  <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                    {desc}
                  </p>
                </div>
              </div>
              {i < 2 && <div style={{ height: '1px', background: '#e9ecef' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="section-pad-lg" style={{ background: WHITE, padding: '100px 0' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div style={{
                display: 'inline-block',
                background: '#E8F5F1', border: '1px solid #B2DFDB',
                borderRadius: '20px', padding: '6px 20px', marginBottom: '24px',
              }}>
                <span style={{ color: TEAL, fontSize: '12px', fontWeight: '700', letterSpacing: '2px' }}>
                  BY THE NUMBERS
                </span>
              </div>
              <h2 style={{
                fontWeight: '900', fontSize: '2.8rem',
                color: DARK, letterSpacing: '-1px', lineHeight: 1.15,
                marginBottom: '20px',
              }}>
                Real results for<br />real Nairobi drivers
              </h2>
              <p style={{ color: MUTED, fontSize: '0.95rem', lineHeight: 1.8, maxWidth: '480px' }}>
                We measure success by how many minutes we save you — not just how many spots we list.
              </p>
              <div style={{ height: '1px', background: '#e9ecef', margin: '32px 0' }} />
              <div className="d-flex gap-5 flex-wrap">
                {[['10,000', 'Daily active users'], ['60%', 'Parking time saved'], ['4.9★', '990k+ reviews']].map(([num, label]) => (
                  <div key={label}>
                    <div style={{ fontSize: '2.8rem', fontWeight: '900', color: DARK, letterSpacing: '-2px', lineHeight: 1 }}>{num}</div>
                    <div style={{ color: '#8B949E', fontSize: '13px', marginTop: '6px' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-6" style={{ position: 'relative' }}>
              <div style={{ borderRadius: '16px', overflow: 'hidden', height: '420px' }}>
                <img
                  src="/images/people-on-spotfinder.webp"
                  alt="Spot Finder users"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{
                position: 'absolute', bottom: '24px', right: '24px',
                background: WHITE, borderRadius: '14px', padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '10px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              }}>
                <Star size={20} color="#F4C542" fill="#F4C542" aria-hidden="true" />
                <span style={{ fontWeight: '900', fontSize: '1.2rem', color: DARK }}>4.9</span>
                <span style={{ color: '#8B949E', fontSize: '13px', fontWeight: '500' }}>990k+ Reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="cta-outer" style={{ background: '#F5F5F5', padding: '40px 24px 100px' }}>
        <div className="cta-inner" style={{
          background: TEAL, borderRadius: '20px',
          padding: '60px 60px 0', overflow: 'visible', position: 'relative',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '24px 24px', borderRadius: '20px', pointerEvents: 'none',
          }} />
          <div className="row align-items-end" style={{ position: 'relative', zIndex: 1 }}>
            <div className="col-lg-7" style={{ paddingBottom: '60px' }}>
              <h2 style={{ fontWeight: '900', fontSize: '2.8rem', color: WHITE, lineHeight: 1.15, letterSpacing: '-1px', marginBottom: '16px' }}>
                Let's find your perfect<br />parking spot today!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', marginBottom: '36px', lineHeight: 1.7, maxWidth: '480px' }}>
                Park smarter with real-time availability, M-Pesa payments, and instant reservations — anytime, anywhere.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <a href="https://play.google.com/store/apps/details?id=com.spotfinder.owner&hl=en"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on Google Play"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Play size={22} fill={WHITE} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>GET IT ON</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>Google Play</div>
                  </div>
                </a>
                <a href="https://apps.apple.com/ke/app/spotfinder-parking-finder/id6572302355"
                  target="_blank" rel="noopener noreferrer"
                  aria-label="Download Spot Finder on App Store"
                  style={{
                    background: DARK, color: WHITE, border: 'none',
                    borderRadius: '10px', padding: '12px 22px',
                    display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none',
                  }}>
                  <Smartphone size={22} color={WHITE} aria-hidden="true" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', lineHeight: 1 }}>Download on the</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', lineHeight: 1.3 }}>App Store</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-flex justify-content-center align-items-end">
              <img
                className="cta-phone"
                src="/images/spotfinder-screen.webp"
                alt="Spot Finder app screen"
                style={{
                  width: '480px', display: 'block',
                  marginBottom: '-80px', marginTop: '-40px',
                  filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.35))',
                  position: 'relative', zIndex: 2,
                }}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}